import { z } from 'zod';
import {
  isParkingCostType,
  type IOfficeMissingInfoFormValues,
  type TMissingOfficeInfoField,
} from '~/domain/OfficeMissingInfo';

/** Every field starts empty; only the asked ones are validated and sent. */
export const OFFICE_MISSING_INFO_DEFAULT_VALUES: IOfficeMissingInfoFormValues = {
  timePerPatient: '',
  isAssistedHygiene: null,
  parkingType: '',
  isParkingFree: null,
  parkingCost: '',
  parkingCostType: '',
  softwares: [],
  insurances: [],
  operatories: '',
};

const formSchema = z.object({
  timePerPatient: z.string(),
  isAssistedHygiene: z.boolean().nullable(),
  parkingType: z.string(),
  isParkingFree: z.boolean().nullable(),
  parkingCost: z.string(),
  parkingCostType: z.enum(['hr', 'day', '']),
  softwares: z.array(z.string()),
  insurances: z.array(z.string()),
  operatories: z.string(),
});

/** Only the asked fields are required; answering "not free" requires both cost and unit. */
export const buildOfficeMissingInfoSchema = (missingFields: TMissingOfficeInfoField[]) =>
  formSchema.superRefine((values, ctx) => {
    const isAsked = (field: TMissingOfficeInfoField) => missingFields.includes(field);
    const requireField = (isFilled: boolean, path: keyof IOfficeMissingInfoFormValues) => {
      if (!isFilled) {
        ctx.addIssue({ code: 'custom', message: 'Required', path: [path] });
      }
    };

    if (isAsked('timePerPatient')) requireField(!!values.timePerPatient, 'timePerPatient');
    if (isAsked('assistedHygiene')) {
      requireField(typeof values.isAssistedHygiene === 'boolean', 'isAssistedHygiene');
    }
    if (isAsked('parkingType')) requireField(!!values.parkingType, 'parkingType');
    if (isAsked('parkingCost')) {
      requireField(typeof values.isParkingFree === 'boolean', 'isParkingFree');
      if (values.isParkingFree === false) {
        requireField(Number(values.parkingCost) > 0, 'parkingCost');
        requireField(isParkingCostType(values.parkingCostType), 'parkingCostType');
      }
    } else if (isAsked('parkingCostType')) {
      requireField(isParkingCostType(values.parkingCostType), 'parkingCostType');
    }
    if (isAsked('softwares')) requireField(values.softwares.length > 0, 'softwares');
    if (isAsked('insurances')) requireField(values.insurances.length > 0, 'insurances');
    if (isAsked('operatories')) requireField(!!values.operatories, 'operatories');
  });
